import { useEffect, useState } from "react";
import { TextPad, hasOpenSheet } from "../shared";
import css from "./CancelReasonPrompt.module.css";

/**
 * Sub-modal triggered by «ОТМЕНИТЬ БРОНЬ» in edit-state. Per B.3b.4 spec:
 *   - red/purple gradient border .cyber-prompt.red variant
 *   - title «ОТМЕНИТЬ БРОНЬ · ПРИЧИНА»
 *   - text input — placeholder «Передумали, конфликт, перенос…»
 *   - actions: ОТМЕНИТЬ БРОНЬ red + ОСТАВИТЬ ghost
 *
 * On confirm, parent submits DELETE /api/bookings/:id with body
 * { cancel_reason } — backend writes to bookings.cancel_reason and pushes
 * `cancelled_booking` audit row.
 */
export interface CancelReasonPromptProps {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const PLACEHOLDER = "Передумали, конфликт, перенос…";

export function CancelReasonPrompt({ onConfirm, onCancel }: CancelReasonPromptProps) {
  const [value, setValue] = useState("");
  /* БМ5: поле причины — readonly-триггер, ввод через TextPad (без autofocus). */
  const [padOpen, setPadOpen] = useState(false);

  /* Esc cancels — но не пока поверх открыт TextPad (hasOpenSheet). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !hasOpenSheet()) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <div
      className={css.overlay}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      role="presentation"
    >
      <div
        className={css.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Причина отмены брони"
      >
        <div className={css.body}>
          <div className={css.title}>Отменить бронь · причина</div>

          <input
            type="text"
            readOnly
            inputMode="none"
            data-pad-host="" /* 06.09: хост TextPad-шита */
            className={css.input}
            placeholder={PLACEHOLDER}
            value={value}
            onClick={() => setPadOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            aria-label="Причина отмены"
            autoComplete="off"
            spellCheck={false}
          />

          <div className={css.hint}>Причина уйдёт в аудит-лог брони.</div>

          <div className={css.actions}>
            <button
              type="button"
              className={`${css.btn} ${css.btnRed}`}
              onClick={submit}
              disabled={!value.trim()}
            >
              ОТМЕНИТЬ БРОНЬ
            </button>
            <button
              type="button"
              className={`${css.btn} ${css.btnGhost}`}
              onClick={onCancel}
            >
              ОСТАВИТЬ
            </button>
          </div>
        </div>
      </div>

      {/* БМ6: причина отмены — TextPad multiline (лимит прежний, 120). */}
      <TextPad
        open={padOpen}
        label="Причина отмены"
        multiline
        maxLength={120}
        placeholder={PLACEHOLDER}
        value={value}
        onCommit={setValue}
        onClose={() => setPadOpen(false)}
      />
    </div>
  );
}
